import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { isEntityScopedChecklistTargetType } from 'constants/checklists';
import { getEntityScopedFieldKey } from 'utils/completeness';

import { selectCompletenessChecklistIdSet } from './completenessChecklists.selectors';
import { selectLoanApplicationChecklistResultsList } from './loanApplicationChecklistResults.selectors';

/**
 * Derives, for a given form, a map of its leaf field ids that are currently
 * failing/incomplete and linked to a checklist item via `fieldRef` (BE-1 / DEV-4380).
 *
 * The authored `fieldRef` is the FULL appFormsTree path
 * (`${section}-${subsection}-${tab}-${form}-${leaf}`), while a FormGenerator field's
 * `fieldId` is the bare leaf. So — exactly like autoLock's `selectFormLockedFields` —
 * we scope to the form (`fieldRef.includes(formName)`) and key by the leaf
 * (`fieldRef.split('-').pop()`) so `FieldPresenceWrapper` can match on its leaf `fieldId`.
 *
 * Keyed by the leaf field id — UNLESS the snapshot is entity-scoped (Borrower/Reo/Income/Asset/
 * Liability; Borrower: DEV-4684, other financial-info rows: DEV-5801), in which case the entry is
 * keyed by `getEntityScopedFieldKey(leaf, entityId)` instead, so a per-row field's "needed" state
 * stays scoped to the SPECIFIC row that failed it rather than lighting every sibling row's form.
 * `FieldPresenceWrapper` resolves the same entityId out of its entity-scoped `fieldPath`
 * (`extractEntityIdFromFieldPath`) to look the entry up. A LoanApplication-scoped snapshot's items
 * keep today's bare-leaf keying untouched.
 * Value: `{ checklistId, itemId, isOverridden, targetId, targetType }`.
 *
 * Design decisions:
 *  - Includes ALL field-linked items whose result is not `true` (failing or not yet evaluated),
 *    PLUS overridden items — which the server flips to `result: true` but which must still
 *    surface so the indicator can render the "Overridden" chip (the accountability trail).
 *  - `isOverridden` is read from the item's top-level `wasOverriden` flag (set once the
 *    override is committed) OR an `Override` entry in `overrideHistory.entries` (set while
 *    the override is still a pending change) — NOT `overrideHistory.wasOverriden`, which the
 *    live payload does not carry (DEV-4468). A pending override carries `wasOverriden: false`
 *    yet flips `result` to `true`, so the entries read is what keeps the chip alive then.
 *  - When two checklists share the same leaf (and, for an entity-scoped field, the same entity),
 *    a non-overridden failing entry takes priority over an overridden one.
 *  - `targetId` (the snapshot's `target.targetId` — the applicationId for a LoanApplication
 *    snapshot, the row id for an entity-scoped one) and `targetType` (`'LoanApplication'` /
 *    `'Borrower'` / `'Reo'` / `'Income'` / `'Asset'` / `'Liability'`) ride along on every entry so
 *    a consumer that needs the exact target (the optimistic-override dispatch AND the server
 *    override write) can disambiguate without re-deriving it — the override must record against
 *    the row's own snapshot, not the loan-wide one (DEV-4728).
 *
 * @param {string} formName
 * @returns selector → `{ [key: string]: { checklistId: string, itemId: string, isOverridden: boolean, targetId: string, targetType: string } }`
 */
const isItemOverridden = ({ wasOverriden, overrideHistory }) =>
  !!wasOverriden || !!overrideHistory?.entries?.some(entry => entry?.change?.type === 'Override');

export const selectIncompleteLinkedFields = memoizeOne(formName =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const map = {};

      if (!formName) {
        return map;
      }

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const targetId = snapshot.target?.targetId;
        const targetType = snapshot.target?.targetType;
        const items = snapshot.status?.result?.items;

        // Entity-scoped snapshots (Borrower/Reo/Income/Asset/Liability; Borrower: DEV-4684, other
        // financial-info rows: DEV-5801) route their items to a per-row key so they don't light
        // every sibling row's form; a LoanApplication snapshot keeps the bare-leaf key exactly as
        // before.
        const entityId = isEntityScopedChecklistTargetType(targetType) ? targetId : null;

        // Only the company's completeness checklist(s) drive the "Needed" highlighting — a visible
        // business/test checklist that happens to link a field must not flag it (DEV-4603).
        if (!checklistId || !items || !completenessChecklistIdSet.has(checklistId)) {
          return;
        }

        items.forEach(item => {
          const { checklistItemId, result, fieldRef } = item;
          const isOverridden = isItemOverridden(item);

          // Keep failing/unevaluated items, and overridden items even though the
          // server flips them to `result: true`, so the "Overridden" chip can render.
          if (!fieldRef || !fieldRef.includes(formName) || (result === true && !isOverridden)) {
            return;
          }

          const leaf = fieldRef.split('-').pop();
          const key = entityId ? getEntityScopedFieldKey(leaf, entityId) : leaf;
          const existing = map[key];

          // Non-overridden entries take priority over overridden ones for the same field.
          if (existing && !existing.isOverridden && isOverridden) {
            return;
          }

          map[key] = { checklistId, itemId: checklistItemId, isOverridden, targetId, targetType };
        });
      });

      return map;
    },
  ),
);
